import { useCallback, useEffect, useRef, useState } from "react";

interface UseDebounceOptions {
  callback: (value: string) => void;
  delay?: number;
  initialValue?: string;
}

export const useDebounce = ({
  callback,
  delay = 300,
  initialValue = "",
}: UseDebounceOptions): {
  handleChange: (value: string) => void;
  value: string;
} => {
  const [value, setValue] = useState(initialValue);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const callbackRef = useRef(callback);
  const hasUserTypedRef = useRef(false);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!hasUserTypedRef.current) {
      setValue(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (value: string): void => {
      hasUserTypedRef.current = true;
      setValue(value);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(value);
        hasUserTypedRef.current = false;
      }, delay);
    },
    [delay],
  );

  return { handleChange, value };
};
